import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, KeyRound, Mic, Upload, ArrowLeft, Loader2, CheckCircle2, Music2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const inputStyle = { background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" };

export default function Submit() {
  const { t, lang } = useLang();
  const [step, setStep] = useState("email"); // email | code | form | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title_en: "", title_si: "", artist: "", singer_name: "", singer_contact: "", year: "", tags: "", notes: "" });
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [consent, setConsent] = useState(false);

  const sendCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/submissions/request-otp`, { email: email.trim().toLowerCase() });
      toast.success(lang === "si" ? "කේතය ඔබේ විද්‍යුත් තැපෑලට එවා ඇත" : "Code sent to your email");
      setStep("code");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/submissions/verify-otp`, { email: email.trim().toLowerCase(), code: code.trim() });
      setToken(data.token);
      setStep("form");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!audioFile) { toast.error(lang === "si" ? "ශ්‍රව්‍ය ගොනුවක් තෝරන්න" : "Please choose an audio file."); return; }
    if (!consent) { toast.error(lang === "si" ? "කරුණාකර කැමැත්ත සලකුණු කරන්න" : "Please tick the consent box."); return; }
    const fd = new FormData();
    fd.append("token", token);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("consent", "true");
    fd.append("audio", audioFile);
    if (coverFile) fd.append("cover", coverFile);
    setBusy(true);
    try {
      await axios.post(`${API}/submissions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStep("done");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep("email"); setEmail(""); setCode(""); setToken("");
    setForm({ title_en: "", title_si: "", artist: "", singer_name: "", singer_contact: "", year: "", tags: "", notes: "" });
    setAudioFile(null); setCoverFile(null); setConsent(false);
  };

  const field = (key, label, opts = {}) => (
    <label className="block">
      <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{label}{opts.required && <span style={{ color: "var(--accent)" }}> *</span>}</span>
      <input
        data-testid={`submit-field-${key}`}
        type={opts.type || "text"}
        required={opts.required}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none"
        style={inputStyle}
      />
    </label>
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col items-center text-center mb-8 sk-fade-up">
          <span className="grid place-items-center w-14 h-14 rounded-2xl sk-glow mb-4" style={{ background: "var(--player-bg)", color: "var(--player-accent)" }}><Mic size={28} /></span>
          <h1 className={`${lang === "si" ? "font-si" : "font-en"} text-3xl sm:text-4xl font-bold`} style={{ color: "var(--text)" }}>{t("submit_title")}</h1>
          <p className="font-ui text-base mt-3 leading-relaxed" style={{ color: "var(--text-2)" }}>{t("submit_sub")}</p>
        </div>

        {step === "email" && (
          <form onSubmit={sendCode} className="rounded-3xl p-6 space-y-4 sk-scale-in" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
            <label className="block">
              <span className="font-ui text-sm font-bold flex items-center gap-2" style={{ color: "var(--text-2)" }}><Mail size={16} /> {t("submit_email_label")}</span>
              <input data-testid="submit-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full h-14 px-4 rounded-xl font-ui text-lg outline-none" style={inputStyle} />
            </label>
            <button data-testid="submit-send-code-button" disabled={busy} type="submit" className="w-full h-14 rounded-xl font-ui font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>
              {busy ? <Loader2 size={20} className="animate-spin" /> : <KeyRound size={20} />} {t("submit_send_code")}
            </button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={verify} className="rounded-3xl p-6 space-y-4 sk-scale-in" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
            <p className="font-ui text-sm" style={{ color: "var(--text-muted)" }}>{email}</p>
            <label className="block">
              <span className="font-ui text-sm font-bold flex items-center gap-2" style={{ color: "var(--text-2)" }}><KeyRound size={16} /> {t("submit_code_label")}</span>
              <input data-testid="submit-code-input" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="mt-1 w-full h-16 px-4 rounded-xl font-ui text-3xl tracking-[0.5em] text-center outline-none" style={inputStyle} />
            </label>
            <button data-testid="submit-verify-button" disabled={busy || code.length < 4} type="submit" className="w-full h-14 rounded-xl font-ui font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>
              {busy ? <Loader2 size={20} className="animate-spin" /> : <CheckCircle2 size={20} />} {t("submit_verify")}
            </button>
            <div className="flex justify-between font-ui text-sm font-semibold pt-1">
              <button type="button" onClick={sendCode} data-testid="submit-resend-button" style={{ color: "var(--accent)" }}>{t("submit_resend")}</button>
              <button type="button" onClick={() => { setStep("email"); setCode(""); }} style={{ color: "var(--text-muted)" }}>{t("submit_change_email")}</button>
            </div>
          </form>
        )}

        {step === "form" && (
          <form onSubmit={submit} className="rounded-3xl p-6 space-y-4 sk-scale-in" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
            <h2 className="font-ui text-xl font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Music2 size={20} /> {t("submit_form_title")}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {field("title_en", t("f_title_en"), { required: true })}
              {field("title_si", t("f_title_si"))}
            </div>
            {field("artist", t("f_artist"), { required: true })}
            <div className="grid sm:grid-cols-2 gap-4">
              {field("singer_name", t("f_singer_name"), { required: true })}
              {field("singer_contact", t("f_contact"))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {field("year", t("f_year"))}
              {field("tags", t("f_tags"))}
            </div>
            {field("notes", t("f_notes"))}
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("f_audio")} <span style={{ color: "var(--accent)" }}>*</span></span>
                <input data-testid="submit-audio-file" type="file" accept="audio/mpeg,.mp3" required onChange={(e) => setAudioFile(e.target.files[0])} className="mt-1 w-full font-ui text-sm" style={{ color: "var(--text-2)" }} />
              </label>
              <label className="block">
                <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("f_cover")}</span>
                <input data-testid="submit-cover-file" type="file" accept="image/*" onChange={(e) => setCoverFile(e.target.files[0])} className="mt-1 w-full font-ui text-sm" style={{ color: "var(--text-2)" }} />
              </label>
            </div>
            <label className="flex items-start gap-3 cursor-pointer rounded-2xl p-4" style={{ background: "var(--brass-light)" }}>
              <input data-testid="submit-consent-checkbox" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-6 h-6 mt-0.5 accent-amber-600 shrink-0" />
              <span className="font-ui text-sm leading-relaxed" style={{ color: "var(--text)" }}>{t("consent_label")}</span>
            </label>
            <button data-testid="submit-song-button" disabled={busy} type="submit" className="w-full h-14 rounded-xl font-ui font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>
              {busy ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />} {t("submit_btn")}
            </button>
          </form>
        )}

        {step === "done" && (
          <div className="rounded-3xl p-8 text-center sk-scale-in" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid="submit-done">
            <span className="grid place-items-center w-16 h-16 rounded-full mx-auto mb-4" style={{ background: "var(--accent)", color: "#fff" }}><CheckCircle2 size={34} /></span>
            <h2 className={`${lang === "si" ? "font-si" : "font-en"} text-2xl font-bold`} style={{ color: "var(--text)" }}>{t("submit_done_title")}</h2>
            <p className="font-ui text-base mt-2" style={{ color: "var(--text-2)" }}>{t("submit_done_sub")}</p>
            <button onClick={reset} data-testid="submit-another-button" className="mt-6 h-12 px-6 rounded-full font-ui font-bold" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>{t("submit_another")}</button>
          </div>
        )}

        <Link to="/rooms/sindu-kamare" className="flex items-center justify-center gap-2 font-ui text-sm font-semibold mt-6" style={{ color: "var(--accent)" }}><ArrowLeft size={16} /> {t("back_room")}</Link>
      </div>
    </div>
  );
}
